import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { apiFxStore, type FxQuote } from "../lib/api";

type FxState = {
  quote: FxQuote;
  request: {
    from_currency: string;
    to_currency: string;
    from_amount: number;
  };
};

export default function FxConfirm() {
  const location = useLocation();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = useMemo<FxState | null>(() => {
    const direct = location.state as FxState | null;
    if (direct?.quote && direct?.request) return direct;

    try {
      const raw = sessionStorage.getItem("finexa.fx.quote");
      return raw ? (JSON.parse(raw) as FxState) : null;
    } catch {
      return null;
    }
  }, [location.state]);

  async function confirm() {
    if (!state) return;
    setSubmitting(true);
    setError(null);

    try {
      const res = await apiFxStore(state.request);
      sessionStorage.removeItem("finexa.fx.quote");
      navigate(res.redirect || "/transactions");
    } catch (err: any) {
      setError(err?.message || "Could not complete the exchange.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!state) {
    return (
      <div className="max-w-xl card-box space-y-4">
        <h1 className="text-xl font-semibold">No FX quote found</h1>
        <p className="text-sm text-gray-600">Create a new FX quote before confirming an exchange.</p>
        <Link to="/fx/create" className="btn">
          Create quote
        </Link>
      </div>
    );
  }

  const quote = state.quote;

  return (
    <div className="max-w-3xl card-box space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Confirm Currency Exchange</h1>
        <p className="text-sm text-gray-500">Review the live rate before executing the exchange.</p>
      </header>

      {error && <div className="alert-danger">{error}</div>}

      <dl className="divide-y divide-gray-100 rounded-lg border border-gray-200">
        <Row label="From currency" value={quote.from_currency} />
        <Row label="To currency" value={quote.to_currency} />
        <Row label="Amount to exchange" value={`${formatMoney(quote.from_amount)} ${quote.from_currency}`} />
        <Row label="Base rate" value={formatRate(quote.base_rate)} mono />
        <Row
          label="Rate after markup"
          value={`${formatRate(quote.rate)} (${formatMoney(quote.fee_markup_percent)}% markup)`}
          mono
        />
        <Row label="You will receive" value={`${formatMoney(quote.converted_amount, 8)} ${quote.to_currency}`} />
        <Row label="FX fee" value={`${formatMoney(quote.fee)} ${quote.to_currency}`} />
      </dl>

      <div className="flex flex-wrap items-center gap-3">
        <button className="btn" onClick={confirm} disabled={submitting}>
          {submitting ? "Completing..." : "Confirm exchange"}
        </button>
        <Link to="/fx/create" className="btn btn-white">
          Back
        </Link>
      </div>
    </div>
  );
}

function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="grid gap-2 px-4 py-3 sm:grid-cols-2">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className={`text-sm font-semibold text-gray-900 sm:text-right ${mono ? "font-mono" : ""}`}>
        {value}
      </dd>
    </div>
  );
}

function formatMoney(value: number | string | null | undefined, max = 2) {
  return Number(value ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: max,
  });
}

function formatRate(value: number | string | null | undefined) {
  return Number(value ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  });
}
